import { DeviceCatalog, DeviceModelDetail } from "@services/catalog/types";
import {
  ColDefWithTab,
  TabValue,
} from "@pages/Assets/common/FilterColumn/context";
import DeviceCardImage from "@common/components/ListDetailViews/DetailView/DeviceCard/DeviceCardImage";
import Button from "@common/components/Button";
import ListTypography from "@common/components/ListDetailViews/ListView/ListTypography";
import { getModelLogo } from "@common/utils/devices/logo";
import ListIndicator from "@common/components/ListDetailViews/ListView/ListIndicator";
import { ListImage } from "@common/components/ListDetailViews/ListView/ListImage";

export const LEFT_FIXED_SIZE = 320;
export const MIN_FIXED_SIZE = 100;

export const DeviceImage = ({ image }: { image: string }) => (
  <DeviceCardImage image={`${__symphony_env__.REACT_APP_SERVER_URL}${image}`} />
);

export const sortingMapper = (key: string) => {
  switch (key) {
    case "defaultDeviceAdapter":
      return "defaultDeviceAdapter.id";
    default:
      return key;
  }
};

export const getColumns = (
  onModelClick: (row: DeviceModelDetail) => void | undefined,
  catalog?: DeviceCatalog
): ColDefWithTab<DeviceModelDetail>[] => {
  return [
    {
      key: "id",
      label: "Device ID",
      tab: [TabValue.Export],
    },
    {
      label: "External Catalog ID",
      key: "externalCatalogId",
      tab: [TabValue.Export],
    },
    {
      key: "model",
      label: "Model Name",
      minWidth: LEFT_FIXED_SIZE,
      sortable: true,
      render: (row, column) => (
        <span
          style={{
            display: "flex",
            width: "100%",
            justifyContent: "center",
            paddingRight: "10%",
            paddingLeft: "10%",
          }}
        >
          <Button
            id={`${row.id}`}
            variant="outlined"
            onClick={() => onModelClick(row)}
            ignoreLocalization
            tooltip={row.model}
            sx={{
              position: "relative",
              width: "100%",
              maxWidth: "100%",
              "& .MuiTypography-root": {
                position: "absolute",
                top: "50%",
                left: "0",
                right: "0",
                transform: "translateY(-50%)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                padding: "0 10px",
              },
            }}
          >
            {row.model}
          </Button>
        </span>
      ),
      tab: [TabValue.Export, TabValue.Info],
    },
    {
      key: "manufacturer",
      label: "Manufacturer",
      sortable: true,
      minWidth: MIN_FIXED_SIZE,
      render: ({ manufacturer }) => <ListTypography cellValue={manufacturer} />,
      tab: [TabValue.Export, TabValue.Info],
    },
    {
      key: "deviceType",
      label: "Type",
      sortable: true,
      minWidth: MIN_FIXED_SIZE,
      render: ({ deviceType }) => <ListTypography cellValue={deviceType} />,
      tab: [TabValue.Export, TabValue.Info],
    },
    {
      label: "Category",
      key: "category",
      sortable: true,
      minWidth: MIN_FIXED_SIZE,
      render: ({ category }) => <ListTypography cellValue={category} />,
      tab: [TabValue.Export, TabValue.Info],
    },
    {
      label: "Family",
      key: "family",
      sortable: true,
      minWidth: MIN_FIXED_SIZE,
      render: ({ family }) => <ListTypography cellValue={family} />,
      tab: [TabValue.Export, TabValue.Info],
    },
    {
      label: "Monitoring Proxy",
      key: "isAggregator",
      sortable: true,
      minWidth: MIN_FIXED_SIZE,
      render: ({ isAggregator }) => <ListIndicator active={isAggregator} />,
      tab: [TabValue.Export, TabValue.Info],
    },
    {
      label: "Direct",
      key: "monitoredDirectly",
      sortable: true,
      minWidth: MIN_FIXED_SIZE,
      render: ({ monitoredDirectly }) => (
        <ListIndicator active={monitoredDirectly} />
      ),
      tab: [TabValue.Export, TabValue.Info],
    },
    {
      label: "From Aggregator",
      key: "monitoredByAggregator",
      sortable: true,
      minWidth: MIN_FIXED_SIZE,
      render: ({ monitoredByAggregator }) => (
        <ListIndicator active={monitoredByAggregator} />
      ),
      tab: [TabValue.Export, TabValue.Info],
    },

    {
      label: "Adapters",
      key: "totalAdapters",
      sortable: true,
      minWidth: MIN_FIXED_SIZE,
      render: ({ totalAdapters }) => (
        <ListTypography cellValue={totalAdapters} />
      ),
      tab: [TabValue.Export, TabValue.Info],
    },
    {
      label: "API Support",
      key: "apiSupport",
      sortable: true,
      minWidth: MIN_FIXED_SIZE,
      render: ({ apiSupport }) => <ListIndicator active={apiSupport} />,
      tab: [TabValue.Export, TabValue.Info],
    },
    {
      label: "SNMP",
      key: "snmpEnabled",
      sortable: true,
      minWidth: MIN_FIXED_SIZE,
      render: ({ snmpEnabled }) => <ListIndicator active={snmpEnabled} />,
      tab: [TabValue.Export, TabValue.Info],
    },
    {
      label: "Network Capable",
      key: "networkCapable",
      sortable: true,
      minWidth: MIN_FIXED_SIZE,
      render: ({ networkCapable }) => <ListIndicator active={networkCapable} />,
      tab: [TabValue.Export, TabValue.Info],
    },
    {
      label: "OIDs",
      key: "totalSnmpOids",
      sortable: true,
      minWidth: MIN_FIXED_SIZE,
      render: ({ totalSnmpOids }) => (
        <ListTypography cellValue={totalSnmpOids} />
      ),
      tab: [TabValue.Export, TabValue.Info],
    },
    {
      label: "Adapter Enabled",
      key: "defaultDeviceAdapter",
      sortable: true,
      minWidth: MIN_FIXED_SIZE,
      render: (row) => (
        <ListIndicator active={Boolean(row.defaultDeviceAdapter?.id)} />
      ),
      csvValue: (row, options) => {
        return `${Boolean(row.defaultDeviceAdapter)}`;
      },
      tab: [TabValue.Export, TabValue.Info],
    },
    {
      label: "Logo",
      sortable: false,
      key: "logo",
      minWidth: MIN_FIXED_SIZE,
      render: (row) => (
        <ListImage
          title={row.model}
          src={
            row.logo
              ? __symphony_env__.REACT_APP_SERVER_URL + row.logo
              : getModelLogo(
                  __symphony_env__.REACT_APP_SERVER_URL,
                  row,
                  catalog
                )
          }
        />
      ),
      tab: [TabValue.Info, TabValue.Export],
    },
  ];
};
